/**
 * The app-side emit host for the team upgrade-request modal (`@heidi/commerce`).
 *
 * Ruling 24: the domain side decides WHEN each event fires and builds the
 * `intl`-formatted label; these functions add the `event_name`, the destination
 * routing, and — for the submit — the `screen_name` of the surface that opened
 * the modal.
 *
 * WHY THE SUBMIT IS SEVEN BINDINGS AND NOT ONE. The modal is mounted globally
 * and its submit reports the ORIGIN's screen, not its own. Counted over both
 * open paths there are seven origins, four of them in other roots:
 * `upgrade_to_clinician_modal`, `upgrade_to_practice_modal`, `settings_billing`
 * and Chat's `credits_remaining_popover`. So the union is not the commerce
 * domain's to declare, and the seam in
 * `@heidi/commerce/contracts` deliberately carries no `screen_name` at all —
 * each origin binds the emitter that names its own screen and passes it to the
 * modal through the open payload, exactly as it already passes `onRequestSent`.
 *
 * The alternative was widening the seam's `screen_name` to `string`, which would
 * have deleted the app's discriminated-union check over the payload; or minting
 * the four foreign screens as commerce tokens, which would put a second spelling
 * of a Chat row inside a commerce package.
 *
 * MODULE-LEVEL CONSTANTS ON PURPOSE. Every one of these is carried as a VALUE
 * through a jotai atom and through a component prop, so an origin that built one
 * per render would put a new identity in the atom on every render of the opener.
 */
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN } from '@/models/analytics/features/trialsAndUpgradesScreenNames';
import {
  TEAM_TRIAL_UPGRADE_REQUEST_ACTION,
  type TrackTeamTrialUpgradeRequestSubmit,
  type TrackTeamTrialUpgradeRequestViewed,
} from '@heidi/commerce/contracts';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * The one `action` token this surface owns, bound to the enum member that
 * declares it. Kept as a map rather than inlined so the token/enum agreement has
 * the same shape here as in the sibling hosts, and so the suite can assert it.
 */
const BUTTON_ACTION_BY_TOKEN = {
  [TEAM_TRIAL_UPGRADE_REQUEST_ACTION.requestAdminUpgrade]:
    ButtonAction.NinetyDayTrialRecapRequestAdminUpgrade,
} as const satisfies Record<
  (typeof TEAM_TRIAL_UPGRADE_REQUEST_ACTION)[keyof typeof TEAM_TRIAL_UPGRADE_REQUEST_ACTION],
  ButtonAction
>;

/** The request modal became visible. The once-per-open guard runs in the domain hook. */
export const trackTeamTrialUpgradeRequestViewed: TrackTeamTrialUpgradeRequestViewed = ({
  screen_name,
  ...rest
}) => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ModalViewed,
    props: {
      screen_name: TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN[screen_name],
      ...rest,
    },
  });
};

const submitEmitter =
  (screenName: ScreenName): TrackTeamTrialUpgradeRequestSubmit =>
  ({ action, ...rest }) => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: screenName,
        action: BUTTON_ACTION_BY_TOKEN[action],
        ...rest,
      },
    });
  };

/**
 * The submit emitter for each origin that can open the request modal.
 *
 * Named constants rather than a lookup keyed by `ScreenName`, because three of
 * the origins are inside `src/features/trialsAndUpgrades` and are the ones this
 * change is removing the enum from — an enum-keyed lookup would hand it straight
 * back to them.
 */
export const trackNinetyDayTrialRecapTeamUpgradeRequestSubmit = submitEmitter(
  ScreenName.NinetyDayTrialRecapModal
);

export const trackClinicianTrialEndedTeamUpgradeRequestSubmit = submitEmitter(
  ScreenName.ClinicianTrialEndedModal
);

export const trackPracticeTrialEndedTeamUpgradeRequestSubmit = submitEmitter(
  ScreenName.PracticeTrialEndedModal
);

export const trackUpgradeToClinicianModalTeamUpgradeRequestSubmit = submitEmitter(
  ScreenName.UpgradeToClinicianModal
);

export const trackUpgradeToPracticeModalTeamUpgradeRequestSubmit = submitEmitter(
  ScreenName.UpgradeToPracticeModal
);

export const trackSettingsBillingTeamUpgradeRequestSubmit = submitEmitter(
  ScreenName.SettingsBilling
);

export const trackCreditsRemainingPopoverTeamUpgradeRequestSubmit = submitEmitter(
  ScreenName.CreditsRemainingPopover
);
